import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchAuctionByAktenzeichen, trackPageView } from '@/services/api';
import { trackVisit } from '@/lib/tracking';
import { useAnalyticsStore } from '@/stores/analyticsStore';
import type { Database } from '@/lib/database.types';
import type { StrategicSection } from '@shared/types';

type AuctionRow = Database['public']['Tables']['auctions']['Row'];

// ============================================
// INSPECTION MODE
// ============================================
function getUniqueSelector(el: Element): string {
  if (el.id) return `#${el.id}`;
  
  const parts: string[] = [];
  let current: Element | null = el;
  
  while (current && current !== document.body) {
    let selector = current.tagName.toLowerCase();
    if (current.id) {
      selector = `#${current.id}`;
      parts.unshift(selector);
      break;
    } else {
      const classes = Array.from(current.classList).filter(c => c && !c.includes(' '));
      if (classes.length > 0) {
        const clsSelector = '.' + classes.slice(0, 2).join('.');
        const siblings = current.parentElement ? Array.from(current.parentElement.children).filter(s => s.tagName === current!.tagName) : [];
        if (siblings.length > 1) {
          const idx = siblings.indexOf(current) + 1;
          selector = `${clsSelector}:nth-of-type(${idx})`;
        } else {
          selector = clsSelector;
        }
      } else {
        const siblings = current.parentElement ? Array.from(current.parentElement.children).filter(s => s.tagName === current!.tagName) : [];
        if (siblings.length > 1) {
          const idx = siblings.indexOf(current) + 1;
          selector = `${selector}:nth-of-type(${idx})`;
        }
      }
      parts.unshift(selector);
    }
    current = current.parentElement;
  }
  
  return parts.join(' > ');
}

function initInspectionMode() {
  const style = document.createElement('style');
  style.id = 'inspection-style';
  style.textContent = `
    .inspect-hover { outline: 2px solid #ff6600 !important; outline-offset: 2px !important; transition: outline 0.1s !important; }
    [data-inspectable]:hover { outline: 2px dashed #ff660066 !important; outline-offset: 2px !important; cursor: crosshair !important; }
  `;
  document.head.appendChild(style);
  
  document.querySelectorAll('button, a, input, textarea, select, [class], label, h1, h2, h3, h4, p, div').forEach(el => {
    (el as HTMLElement).dataset.inspectable = 'true';
  });
  
  document.addEventListener('click', handleInspectClick, true);
}

let inspectHandler: ((e: MouseEvent) => void) | null = null;

function handleInspectClick(e: MouseEvent) {
  e.preventDefault();
  e.stopPropagation();
  
  const el = e.target as HTMLElement;
  el.classList.add('inspect-hover');
  setTimeout(() => el.classList.remove('inspect-hover'), 1000);
  
  const selector = getUniqueSelector(el);
  navigator.clipboard.writeText(selector).catch(() => {});
  
  console.log('[Inspect] Selector:', selector);
}

// ============================================
// TENDER CAROUSEL
// ============================================

const TENDER_IMAGES = [
  'https://nx67536.your-storageshare.de/s/WgFWHy7WKdQjDRs/preview',
  'https://nx67536.your-storageshare.de/s/YaGmycZE3MP3ndi/preview',
  'https://nx67536.your-storageshare.de/s/YSMnD8PesmccDCT/preview',
  'https://nx67536.your-storageshare.de/s/Tz9jXqXygEpdAE6/preview',
  'https://nx67536.your-storageshare.de/s/K5epn83RyZGLFQr/preview',
  'https://nx67536.your-storageshare.de/s/67XHiEPdxwjznzm/preview',
];

function TenderCarousel() {
  const [current, setCurrent] = useState(0);

  const prev = () => setCurrent(i => (i - 1 + TENDER_IMAGES.length) % TENDER_IMAGES.length);
  const next = () => setCurrent(i => (i + 1) % TENDER_IMAGES.length);

  return (
    <div className="relative rounded-xl overflow-hidden shadow-lg select-none">
      <img
        src={TENDER_IMAGES[current]}
        alt={`Drone tender example ${current + 1}`}
        className="w-full h-auto object-cover transition-opacity duration-300"
      />
      {/* Prev / Next */}
      <button
        onClick={prev}
        className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors"
        aria-label="Previous"
      >&#8249;</button>
      <button
        onClick={next}
        className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors"
        aria-label="Next"
      >&#8250;</button>
      {/* Dots */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
        {TENDER_IMAGES.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`w-2 h-2 rounded-full transition-colors ${i === current ? 'bg-white' : 'bg-white/40'}`}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function Patent11914489Landing() {
  const [inquirySent, setInquirySent] = useState(false);
  const [phone, setPhone] = useState('');
  const [requestCall, setRequestCall] = useState(true);
  const { setSectionVisible, setSectionHidden, flushSections, setPageViewId } = useAnalyticsStore();
  const [searchParams] = useSearchParams();

  // Tracking
  useEffect(() => {
    trackVisit({
      page: window.location.pathname,
      email: searchParams.get('email'),
      referred_company: searchParams.get('company'),
    }).catch(err => console.error('Visitor tracking error:', err));
  }, []);

  // Inspection mode
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search.includes('inspect')) {
      initInspectionMode();
      return () => {
        document.head.querySelector('#inspection-style')?.remove();
        document.removeEventListener('click', handleInspectClick, true);
        document.querySelectorAll('[data-inspectable]').forEach(el => delete (el as HTMLElement).dataset.inspectable);
      };
    }
  }, []);

  async function handleInquiry(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const response = await fetch('https://fojubejaymmnxqsulnql.supabase.co/functions/v1/patent-form-submission', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subject: 'Patent 11914489',
        company: fd.get('company') as string,
        email: fd.get('email') as string,
        message: fd.get('message') as string,
        phone: phone || undefined,
        request_call: phone ? requestCall : false,
      }),
    });

    const result = await response.json();
    if (result.ok || !result.error) {
      setInquirySent(true);
    }
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8] text-[#2C2C24] relative">
      {/* Grain Texture Overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03] z-50 mix-blend-multiply" 
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")` }}
      />

      {/* Hero */}
      <section className="relative py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Organic blob background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-[60%_40%_30%_70%] bg-[#5D7052]/10 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-[40%_60%_70%_30%] bg-[#C18C5D]/10 blur-3xl" />
        </div>
        
        <div className="max-w-5xl mx-auto relative">
          <p className="text-sm tracking-[0.2em] uppercase text-[#78786C] mb-2"><span className="font-bold text-[#5D7052]">for SALE</span> · Patent #{'11914489'}</p>
          <h1 className="text-5xl md:text-7xl font-sans font-semibold leading-[1.1] mb-8">
            Strategic IP Protection for Defence Contracts
          </h1>
          <p className="text-xl md:text-2xl text-[#78786C] mb-4 max-w-2xl leading-relaxed">
            Protect position in defence tenders and prevent competitors from using IP against you.
          </p>
          <p className="text-lg md:text-xl text-[#78786C] mb-6 max-w-2xl leading-relaxed">
            This patent "Redundant Control System for Distributed Automation" is a novel concept well suited to secure operations of drones (air, sea, land) in adversarial environments.
          </p>
          
          <p className="text-xl md:text-2xl text-[#2C2C24] leading-relaxed mb-4">
            Buying this patent will enable you to:
          </p>
          <ul className="text-lg md:text-xl text-[#78786C] leading-relaxed space-y-3 mb-12 list-none pl-0">
            <li className="flex items-start gap-3">
              <span className="text-[#5D7052] mt-1">✓</span>
              <span>Secure tenders, outperforming your competition</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#5D7052] mt-1">✓</span>
              <span>Outplay competitors who already build drone systems but ignore this patent</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#5D7052] mt-1">✓</span>
              <span>Grant a company a licence to use this patent for tech they are already selling</span>
            </li>
          </ul>
          
          
        </div>
      </section>

      {/* Countdown */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#5D7052] text-white">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-8 text-center">
          <div>
            <p className="text-2xl md:text-3xl font-sans font-semibold">Deadline: <span className="bg-yellow-300 text-gray-900 px-2 py-1 rounded">May 16, 2026</span>, 12:00 PM CET</p>
          </div>
          <div>
            <p className="text-xl md:text-2xl font-sans font-semibold" suppressHydrationWarning>{(() => {
              const diff = new Date('2026-05-16T12:00:00+02:00').getTime() - Date.now();
              if (diff <= 0) return 'Closed';
              const days = Math.floor(diff / (1000 * 60 * 60 * 24));
              const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
              return `${days}d ${hours}h remaining`;
            })()}</p>
          </div>
        </div>
      </section>

      {/* This is for you if */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 bg-[#F0EBE5]/30">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-sans font-semibold mb-8">This is for you if:</h2>
          <ul className="text-lg md:text-xl text-[#78786C] leading-relaxed space-y-3 list-none pl-0">
            <li className="flex items-start gap-3">
              <span className="text-[#5D7052] mt-1">•</span>
              <span>Your company is operating in the defence sector</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#5D7052] mt-1">•</span>
              <span>You participate in tenders for defence technology</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#5D7052] mt-1">•</span>
              <span>Your products are related to drones (air, land, sea)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#5D7052] mt-1">•</span>
              <span>You have ambitions to participate in the defence-IP economics</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Summary */}
      <section id="details" className="py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-sans font-semibold mb-8">Patent Scope</h2>
          <div className="prose prose-lg text-[#78786C] leading-relaxed">
            <p className="text-xl leading-8">
              This patent describes a <strong className="text-[#5D7052]">Redundant Control System for Distributed Automation</strong> — a method for securing real-time automation systems against communication failures. 
            </p>
          </div>
          <div className="mt-8 rounded-lg overflow-hidden">
            <img 
              src="https://nx67536.your-storageshare.de/s/86P9AwxTqKXmFKo/preview" 
              alt="Patent diagram" 
              className="w-full h-auto"
            />
          </div>
          <p className="mt-8 text-lg leading-relaxed text-[#78786C]">
            The patent secures the usage of a hybrid reasoning or navigation system. One part operates remotely on a server, and if the connection is interrupted, the low-effort reasoning is activated on the robot to secure its mission.
          </p>
          <div className="mt-8 text-center">
            <a 
              href="https://patentimages.storage.googleapis.com/c8/12/ce/12c52a20868735/US11914489.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-12 px-8 bg-[#5D7052] text-[#F3F4F1] rounded-full font-semibold hover:scale-105 transition-all duration-300"
            >
              Read Full Patent
            </a>
          </div>
        </div>
      </section>

      {/* How to secure tenders */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 bg-[#F0EBE5]/30">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-sans font-semibold mb-8">How to secure tenders</h2>
          <p className="text-lg text-[#78786C] leading-relaxed mb-6">
            Hundreds of robotic drone tenders are opened each month. If you write these magic lines in your tender submission:
          </p>
          <blockquote className="border-l-4 border-[#5D7052] pl-6 py-2 mb-8 bg-white rounded-r-lg shadow-sm">
            <p className="text-lg italic text-[#2C2C24] leading-relaxed">
              "The hybrid remote-communication approach described above is protected by our patent #11,914,489."
            </p>
          </blockquote>
          <p className="text-lg text-[#78786C] leading-relaxed mb-4">This will likely happen:</p>
          <ol className="text-lg text-[#78786C] leading-relaxed space-y-4 list-none pl-0 mb-12">
            <li className="flex items-start gap-4">
              <span className="text-[#5D7052] font-bold min-w-[2rem]">1.</span>
              <span>They will see you as a solid partner, well prepared for collaboration in the long run.</span>
            </li>
            <li className="flex items-start gap-4">
              <span className="text-[#5D7052] font-bold min-w-[2rem]">2.</span>
              <span>The procurement will try to clarify whether they are even allowed to contract anyone else, because there is no guarantee you won't claim IP rights on the technology most applicants are using. Given that 2026 defence tenders often run on timelines as short as one month, this review rarely concludes in time, making you the straightforward choice.</span>
            </li>
            <li className="flex items-start gap-4">
              <span className="text-[#5D7052] font-bold min-w-[2rem]">3.</span>
              <span>They might ask you to grant the licence of your patent even if you don't win the tender.</span>
            </li>
          </ol>

          {/* Tender carousel */}
          <p className="text-sm font-bold text-[#78786C] uppercase tracking-widest mb-4">Huge potential for you in 2026</p>
          <TenderCarousel />
        </div>
      </section>

      {/* Example Projects */}
      <section className="py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-sans font-semibold mb-12 text-center">Use Cases</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-[#F0EBE5] rounded-lg overflow-hidden hover:scale-[1.02] transition-all duration-500">
              <div className="h-64 bg-gradient-to-br from-[#5D7052]/20 to-[#C18C5D]/20 flex items-center justify-center">
                <img src="https://nx67536.your-storageshare.de/s/ykKXAm8YC5jErcz/preview" alt="Air Drone" className="w-full h-full object-cover" />
              </div>
              <div className="p-8">
                <h3 className="text-xl font-sans font-semibold mb-3">Air Drones</h3>
                <p className="text-[#78786C] leading-relaxed">Autonomous drone systems for surveillance and transport in combat zones with redundant control.</p>
              </div>
            </div>
            <div className="bg-[#F0EBE5] rounded-lg overflow-hidden hover:scale-[1.02] transition-all duration-500">
              <div className="h-64 bg-gradient-to-br from-[#5D7052]/20 to-[#C18C5D]/20 flex items-center justify-center">
                <img src="https://nx67536.your-storageshare.de/s/GWi56yjP9eF9dG8/preview" alt="Sea Drone" className="w-full h-full object-cover" />
              </div>
              <div className="p-8">
                <h3 className="text-xl font-sans font-semibold mb-3">Sea Drones</h3>
                <p className="text-[#78786C] leading-relaxed">Autonomous underwater vehicles for mine clearance and reconnaissance with fail-safe communication.</p>
              </div>
            </div>
            <div className="bg-[#F0EBE5] rounded-lg overflow-hidden hover:scale-[1.02] transition-all duration-500">
              <div className="h-64 bg-gradient-to-br from-[#5D7052]/20 to-[#C18C5D]/20 flex items-center justify-center">
                <img src="https://nx67536.your-storageshare.de/s/be8TX3z3FE6XZJC/preview" alt="Land Drone" className="w-full h-full object-cover" />
              </div>
              <div className="p-8">
                <h3 className="text-xl font-sans font-semibold mb-3">Land Drones</h3>
                <p className="text-[#78786C] leading-relaxed">Autonomous vehicles for logistics and evacuation with redundant control during radio interference.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What you should do next */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 bg-[#F0EBE5]/30">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-sans font-semibold mb-8">What you should do next</h2>
          <ol className="text-lg md:text-xl text-[#78786C] leading-relaxed space-y-4 list-none pl-0">
            <li className="flex items-start gap-4">
              <span className="text-[#5D7052] font-bold min-w-[2rem]">1.</span>
              <span>Submit your interest and receive detailed information about the next steps</span>
            </li>
            <li className="flex items-start gap-4">
              <span className="text-[#5D7052] font-bold min-w-[2rem]">2.</span>
              <span>Calculate your potential gains you might achieve with this patent</span>
            </li>
            <li className="flex items-start gap-4">
              <span className="text-[#5D7052] font-bold min-w-[2rem]">3.</span>
              <span>Submit your offer</span>
            </li>
            <li className="flex items-start gap-4">
              <span className="text-[#5D7052] font-bold min-w-[2rem]">4.</span>
              <span>The highest bid wins - on <span className="bg-yellow-300 text-gray-900 px-2 py-1 rounded">May 16, 2026</span> 12:00 PM CET</span>
            </li>
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section id="inquiry" className="py-32 px-4 sm:px-6 lg:px-8 bg-[#5D7052] text-white relative overflow-hidden">
        {/* Organic blob backgrounds */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-[60%_40%_30%_70%] bg-[#5D7052]/50 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-[40%_60%_70%_30%] bg-[#C18C5D]/30 blur-3xl" />
        </div>
        
        <div className="max-w-2xl mx-auto text-center relative">
          <h2 className="text-4xl font-sans font-semibold mb-4">Register Your Interest</h2>
          <p className="text-[#F3F4F1]/80 mb-10 text-lg">
            Submit your interest below. We will contact you regarding the next steps.
          </p>
          
          {inquirySent ? (
            <div className="p-8 bg-[#5D7052]/50 rounded-[2rem] border border-[#F3F4F1]/20">
              <p className="text-[#E6DCCD] font-sans font-semibold text-xl mb-2">Thank you for your inquiry!</p>
              <p className="text-[#F3F4F1]/70">We will be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleInquiry} className="space-y-6 text-left">
              <input type="hidden" name="subject" value="Interest Patent 11914489" />
              <div>
                <label className="block text-sm mb-2 text-[#F3F4F1]/80">Company *</label>
                <input name="company" required className="w-full h-12 px-6 rounded-full bg-white/10 border border-[#F3F4F1]/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#E6DCCD]/30 focus:ring-offset-2 focus:ring-offset-[#5D7052]" />
              </div>
              <div>
                <label className="block text-sm mb-2 text-[#F3F4F1]/80">Email *</label>
                <input name="email" type="email" required className="w-full h-12 px-6 rounded-full bg-white/10 border border-[#F3F4F1]/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#E6DCCD]/30 focus:ring-offset-2 focus:ring-offset-[#5D7052]" />
              </div>
              <div>
                <label className="block text-sm mb-2 text-[#F3F4F1]/80">Phone</label>
                <input
                  name="phone"
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  className="w-full h-12 px-6 rounded-full bg-white/10 border border-[#F3F4F1]/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#E6DCCD]/30 focus:ring-offset-2 focus:ring-offset-[#5D7052]"
                />
                {phone && (
                  <label className="flex items-center gap-3 mt-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={requestCall}
                      onChange={e => setRequestCall(e.target.checked)}
                      className="w-4 h-4 accent-[#C18C5D]"
                    />
                    <span className="text-sm text-[#F3F4F1]/80">I would like to receive a call</span>
                  </label>
                )}
              </div>
              <div>
                <label className="block text-sm mb-2 text-[#F3F4F1]/80">Message</label>
                <textarea name="message" rows={4} className="w-full px-6 py-4 rounded-[2rem] bg-white/10 border border-[#F3F4F1]/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#E6DCCD]/30 focus:ring-offset-2 focus:ring-offset-[#5D7052]" />
              </div>
              <button type="submit" className="w-full h-14 bg-[#C18C5D] text-white font-semibold rounded-full hover:scale-105 transition-all duration-300 shadow-[0_4px_20px_-2px_rgba(193,140,93,0.4)] hover:shadow-[0_8px_30px_-4px_rgba(193,140,93,0.5)]">
                Register Interest
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-[#2C2C24] text-[#F3F4F1]/60 text-sm">
        <div className="max-w-5xl mx-auto flex flex-wrap justify-between gap-6">
          <div className="space-y-2">
            <p>Patent #{'11914489'} • US 11914489</p>
            <p>Dr. Eugen Funk, Thomas Staufenbiel, Dr. Jens Lambrecht, Dr. Duy Nguyen</p>
            <p>Granted: February 27, 2024</p>
          </div>
          <div className="flex gap-8">
            <Link to="/impressum" className="hover:text-white transition-colors">Legal Notice</Link>
            <Link to="/datenschutz" className="hover:text-white transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}